import { useState, useEffect, useRef } from "react";
import { wabaApi } from "../../lib/wabaApi";

declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

const META_APP_ID = "843063592187222";
// Configuração v4 (Embedded Signup, só WhatsApp Cloud API). A versão é da
// configuração, não de parâmetro: config criada com seleção de produtos = v4.
const META_CONFIG_ID = "4391459791071365";

interface Props {
  connectionId: string;
  onSuccess: () => void;
}

type EmbeddedSignupSession = {
  phone_number_id?: string;
  waba_id?: string;
  business_id?: string;
  raw?: any;
};

export function EmbeddedSignup({ connectionId, onSuccess }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionRef = useRef<EmbeddedSignupSession>({});

  // Carrega Facebook SDK
  useEffect(() => {
    if (document.getElementById("fb-jssdk")) return;
    window.fbAsyncInit = () => {
      window.FB.init({ appId: META_APP_ID, autoLogAppEvents: true, xfbml: true, version: "v25.0" });
    };
    const script = document.createElement("script");
    script.id = "fb-jssdk";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true; script.defer = true;
    document.body.appendChild(script);
    return () => { document.getElementById("fb-jssdk")?.remove(); };
  }, []);

  // Escuta eventos do Embedded Signup
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const allowedOrigins = ["https://www.facebook.com", "https://web.facebook.com"];
      if (!allowedOrigins.includes(event.origin)) return;
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type !== "WA_EMBEDDED_SIGNUP") return;
        console.log("WA_EMBEDDED_SIGNUP event:", data);

        const isFinish = data?.event === "FINISH" || data?.event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING";
        if (isFinish) {
          sessionRef.current = {
            phone_number_id: data.data?.phone_number_id,
            waba_id: data.data?.waba_id,
            business_id: data.data?.business_id ?? data.data?.businessId,
            raw: data,
          };
          console.log("Embedded Signup finalizado:", sessionRef.current);
        }
        if (data?.event === "ERROR") {
          console.error("Erro no Embedded Signup:", data.data);
          setError(data.data?.error_message || data.data?.message || "Erro no cadastro da Meta.");
          setLoading(false);
        }
        if (data?.event === "CANCEL") {
          setError("Cadastro cancelado.");
          setLoading(false);
        }
      } catch {}
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  async function doLink(code: string) {
    const { phone_number_id, waba_id, business_id } = sessionRef.current;
    console.log("Vinculando WABA:", { code, phone_number_id, waba_id, business_id });

    if (!phone_number_id || !waba_id) {
      setError("Não foi possível obter o número ou a conta WABA da Meta. Tente novamente.");
      setLoading(false); return;
    }
    try {
      await wabaApi.post(`/connections/${connectionId}/link`, {
        code, phone_number_id, waba_id, business_id,
      });
      onSuccess();
    } catch (err: any) {
      console.error("Erro ao vincular número:", err);
      setError(err?.payload?.detail ?? "Erro ao vincular número.");
    } finally { setLoading(false); }
  }

  function handleSignup() {
    if (!window.FB) { setError("SDK da Meta ainda não carregou. Aguarde e tente novamente."); return; }
    setError(null); setLoading(true);
    sessionRef.current = {};

    window.FB.login((response: any) => {
      console.log("FB.login response:", response);
      const code = response?.authResponse?.code;
      if (!code) {
        setError("Cadastro cancelado ou código não retornado pela Meta.");
        setLoading(false); return;
      }
      doLink(code);
    }, {
      config_id: META_CONFIG_ID,
      response_type: "code",
      override_default_response_type: true,
      extras: {
        setup: {},
        // v4 não usa sessionInfoVersion; o coex continua via feature_type
        // (doc: "WhatsApp Business app user onboarding continues to be
        // supported through the feature_type parameter").
        featureType: "whatsapp_business_app_onboarding",
      },
    });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <button
        onClick={handleSignup}
        disabled={loading}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "9px 18px", background: loading ? "var(--surface-input)" : "#25D366",
          color: loading ? "var(--ink-muted)" : "#fff", border: "none", borderRadius: 8,
          fontSize: 13, fontWeight: 600, cursor: loading ? "default" : "pointer",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        {loading ? "Conectando..." : "Conectar WhatsApp Business"}
      </button>
      {error && <span style={{ fontSize: 12, color: "var(--danger)" }}>{error}</span>}
    </div>
  );
}
